import { translate, useMatchSmallViewport } from '@avensia/excite';
import { SlideHeightAnimation } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import * as React from 'react';
import { edgeMarginsDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { Theme } from 'webapp/theme';
import {
  createMaxSmallViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { applyPercentagePrice } from './helpers';
import ProductListCarousel from './ProductListCarousel';
import { CloseIcon } from 'webapp/components/icons';
import { FieldSize } from 'webapp/components/field-size';
import { Button } from 'webapp/components/button';
import { LoadingProductList } from 'webapp/product/ProductList';

type Props = {
  sku: string;
  accessories: NordicNest.Api.ProductAccessoryInfoApiModel[];
  visible: boolean;
  close: () => void;
};

type accessoryProp = NordicNest.Api.PartialProductApiModel & { accessoryToSku: string };

export const UpSellAccessories = React.memo(({ sku, accessories, visible, close }: Props) => {
  const accessoryProducts = accessories?.map(
    ({ accessoryProduct, discountPercent }): accessoryProp => ({
      ...accessoryProduct,
      accessoryToSku: sku,
      price: accessoryProduct.price,
    }),
  );

  return (
    <Base visible={visible}>
      <Content>
        <TopContent>
          <Heading>{translate(t => t.productPage.upSellAccessoriesHeading)}</Heading>
          <CloseButton
            aria-label={translate(t => t.ariaLabelCloseButton)}
            size={FieldSize.Small}
            as={Theme.Transparent}
            onClick={close}
            disableRippleEffect
            square
          >
            <CloseIcon />
          </CloseButton>
        </TopContent>
        <ProductListCarousel id={'upsell_accessories_' + sku} name="upsell_accessories" products={accessoryProducts} />
      </Content>
    </Base>
  );
});

type StickyCrossSellProps = {
  sku: string;
  products: NordicNest.Api.PartialProductApiModel[];
  visible: boolean;
  close: () => void;
};

export const StickyCrossSell = ({ sku, products, visible, close }: StickyCrossSellProps) => {
  const smallViewport = useMatchSmallViewport();
  return (
    <Base visible={visible}>
      <Content>
        <TopContent>
          <Heading>{translate(t => t.productPage.relateProductsCrossSellHeading)}</Heading>
          <CloseButton
            aria-label={translate(t => t.ariaLabelCloseButton)}
            size={FieldSize.Small}
            as={Theme.Transparent}
            onClick={close}
            disableRippleEffect
            square
          >
            <CloseIcon />
          </CloseButton>
        </TopContent>
        {products.length > 0 ? (
          <ProductListCarousel id={'sticky_cross_sell_' + sku} name="sticky_cross_sell" products={products} />
        ) : (
          <LoadingProductList count={smallViewport ? 2 : 4} />
        )}
      </Content>
    </Base>
  );
};

const Base = styled(SlideHeightAnimation);

const TopContent = styled.div(
  createMinMediumViewportDecorator({
    marginBottom: t => t.sharedSpacing.l,
  }),
  {
    position: 'relative',
    marginBottom: t => t.sharedSpacing.m,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
);

const CloseButton = styled(
  Button,
  createMinLargeViewportDecorator({
    right: 44,
  }),
  {
    right: 0,
    position: 'absolute',
  },
);

const Content = styled.div(
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  createMinLargeViewportDecorator({
    padding: { top: t => t.sharedSpacing.l },
  }),
  {
    padding: { x: 0, top: t => t.sharedSpacing.m },
  },
);

const Heading = styled.h2(
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.m,
  }),
  {
    fontWeight: 'bold',
    fontSize: t => t.sharedFontSize.xl,
    marginBottom: 0,
    textAlign: 'center',
  },
);
